/**
 * Safely parse a JSON string field that may already be parsed.
 *
 * Use cases:
 * - API responses where fields may be JSON strings or already-parsed objects
 * - Safe handling of malformed data from external sources
 * - Fields that may be null, undefined, or pre-parsed
 *
 * @param value - The value to parse (may be string, object, null, or undefined)
 * @param defaultValue - The default value to return if parsing fails or value is null/undefined
 * @returns The parsed value, original value if not a string, or defaultValue on error
 *
 * @example
 * // Already parsed object - returns as-is
 * parseJsonField({ key: 'value' }, {}) // { key: 'value' }
 *
 * @example
 * // JSON string - parses and returns
 * parseJsonField('["a", "b"]', []) // ['a', 'b']
 *
 * @example
 * // Malformed JSON - returns default
 * parseJsonField('not valid json', []) // []
 *
 * @example
 * // Null/undefined - returns default
 * parseJsonField(null, []) // []
 * parseJsonField(undefined, []) // []
 */
export function parseJsonField<T>(
  value: string | T | null | undefined,
  defaultValue: T
): T {
  // Return default for null or undefined
  if (value === null || value === undefined) {
    return defaultValue;
  }

  // Return directly if not a string (already parsed)
  if (typeof value !== 'string') {
    return value;
  }

  // Parse JSON string with error handling
  try {
    return JSON.parse(value) as T;
  } catch {
    return defaultValue;
  }
}
